/**
 * D5 · পেমেন্ট মেথড — `/checkout/payment`
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame D5 (360 × 680).
 *
 * Frame note: "A sheet, so the total stays behind it. The partner disclosure sits with
 * the cash line, and 'ব্যালেন্স' appears nowhere in the flow." The word is never used:
 * the concept is অপ্রাপ্য ক্যাশ (docs/02 § terminology).
 *
 * "হাতে হাতে দেব" is a listed method, in the position the frame gives it — not a
 * fallback appended after the real ones, and not a position this screen chooses: the
 * server returns the three in order.
 *
 * ── WIRED, AND THIS IS THE ONE THAT MOVES MONEY ──────────────────────────────
 * The sheet renders `POST /v1/checkout/quote`'s payment block, and নিশ্চিত করুন is
 * `POST /v1/orders` carrying an `Idempotency-Key` minted ONCE when this screen mounts.
 * A double tap therefore sends the same key and the server answers the first response
 * instead of placing a second order — and nothing below the button retries, because
 * `api/client.ts` never retries a non-idempotent request.
 *
 * DEVIATIONS reported rather than resolved:
 *  · The panel's shadow is drawn `0 -2px 14px rgba(14,58,69,.16)`. docs/02 defines two
 *    shadows, `.10` and `.14`; the tokens preset has no `.16`, so it is an inline style.
 *  · § 04's `Sheet` is not used. Its geometry is `padding:14px 15px 15px` with a 19px
 *    title and label-only options; this sheet is `18px 16px` with a 20px title, a
 *    subtitle, a two-line option, an action row and a disclosure box. Forcing the
 *    component would substitute design-system defaults for values the frame states,
 *    which is the substitution docs/01 exists to prevent. Which is canonical is a
 *    design question, and it is in the report.
 */
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@oprappo/ui';
import type { CheckoutQuoteResponse, OfferedPaymentMethod } from '@oprappo/contracts';
import { assertCheckoutQuoteRenderable } from '@oprappo/contracts';
import {
  Frame,
  MANROPE_THEN_ANEK_CLASS,
  PARTNER_ROLE_BN,
  PlusIcon,
  Radio,
  SAND_FRAME,
} from '../offering/chrome.js';
import { Loaded, commerce, orderIdempotencyKey, useResource } from '../offering/commerce.js';

/**
 * ── THE MANUAL PANEL: A NUMBER AND AN AMOUNT, AND NOT ONE INVENTED SENTENCE ──
 *
 * A manual method is one the customer pays from their own phone — they open bKash, send
 * the money to the merchant number, and type the transaction id back. `design/` draws no
 * such panel: there is no frame anywhere in `Oprappo P0 Screens.dc.html` with a merchant
 * number on it and no frame with a transaction-id field.
 *
 * So this renders the two things that are DATA — the number and the server-formatted
 * amount — and renders the founder's own sentence above them WHEN THERE IS ONE. The
 * sentence lives in `payment_method_config.instruction_bn`, which the founder types into
 * the ops console; until then it is the empty string with `copy_status: 'copy_missing'`
 * beside it, and this panel draws no sentence rather than a plausible one nobody read.
 *
 * `data-copy-status` carries that fact into the DOM, so "has anyone written this?" is
 * answerable from a rendered page and not only from a migration comment
 * (`contracts § authored-copy`).
 *
 * The transaction-id field is the same bargain: it is drawn only when the founder has
 * written a label for it, because an unlabelled input on a money screen is worse than an
 * absent one. `POST /v1/payments/claims` exists and is tested either way, so an operator
 * can record the claim today while the copy is being written.
 *
 * NOTHING BELOW RENDERS ON A DRAWN SCREEN. The three drawn methods carry no manual
 * configuration, so `m.manual` is undefined on every row the visual gate scores and this
 * whole subtree is absent from the frame it measures.
 */
function ManualPanel({ method }: { method: OfferedPaymentMethod }): JSX.Element | null {
  const manual = method.manual;
  if (manual === undefined) return null;
  return (
    <div
      className="bg-sand border border-border px-[13px] py-[11px] mt-[16px]"
      data-copy-status={manual.copy_status}
      data-dummy={manual.is_dummy ? 'true' : 'false'}
    >
      {manual.instruction_bn === '' ? null : (
        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
          {manual.instruction_bn}
        </div>
      )}
      <div className="flex justify-between items-baseline gap-[11px] mt-[6px]">
        <span className="font-manrope font-semibold text-[15.5px] leading-[1.35] text-ink tabular-nums">
          {manual.merchant_number}
        </span>
        <span className="font-manrope font-bold text-[15.5px] leading-[1.35] text-ink tabular-nums">
          {manual.amount.display}
        </span>
      </div>
    </div>
  );
}

export function D5PaymentView({
  quote,
  onConfirm,
}: {
  quote: CheckoutQuoteResponse;
  onConfirm?: (methodId: string) => void;
}): JSX.Element {
  const q = assertCheckoutQuoteRenderable(quote);
  const sheet = q.payment;
  const [methodId, setMethodId] = useState<string>(sheet.methods[0]?.id ?? '');
  const selected = sheet.methods.find((m) => m.id === methodId) as
    | OfferedPaymentMethod
    | undefined;

  return (
    <Frame className={`${SAND_FRAME} overflow-hidden`}>
      {/* The checkout behind the sheet, at the drawn `opacity:.3`. */}
      <div className="p-[16px] opacity-30 shrink min-h-0 overflow-hidden" aria-hidden>
        <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">
          {sheet.backdrop_title_bn}
        </div>
        <div className="bg-white border border-border h-[64px] box-content mt-[14px]" />
      </div>
      <div className="flex-1" />

      <div
        role="dialog"
        aria-label={sheet.title_bn}
        className="bg-white border-t border-border px-[16px] py-[18px] shadow-[0_-2px_14px_rgba(14,58,69,.16)]"
      >
        <div className="flex justify-center mb-[16px]">
          <div className="w-[36px] h-[3px] bg-border" />
        </div>
        <div className="font-anek font-semibold text-[20px] leading-[1.75] text-ink">
          {sheet.title_bn}
        </div>
        {/* `সর্বমোট ৳ 1,190` — one sentence from the server, not a prefix plus a figure. */}
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted mt-[4px]">
          {sheet.total_line_bn}
        </div>

        <div className="mt-[16px]">
          {sheet.methods.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMethodId(m.id)}
              aria-pressed={m.id === methodId}
              className="w-full py-[14px] border-t border-border flex justify-between items-center gap-[11px] bg-transparent px-0 rounded-none text-left"
            >
              <span className="flex-1">
                <span className="block font-anek font-medium text-[15.5px] leading-[1.75] text-ink">
                  {m.label_bn}
                </span>
                {m.detail_bn === undefined ? null : (
                  // `Manrope,'Anek Bangla'` as drawn: Manrope sets `1,240.50`, Anek sets আছে.
                  <span
                    className={`block font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px] tabular-nums ${MANROPE_THEN_ANEK_CLASS}`}
                  >
                    {m.detail_bn}
                  </span>
                )}
              </span>
              <Radio selected={m.id === methodId} />
            </button>
          ))}

          <div className="py-[14px] border-t border-border flex gap-[11px] items-center">
            <PlusIcon size={18} />
            <button
              type="button"
              className="font-anek font-medium text-[15.5px] leading-[1.75] text-teal bg-transparent border-0 p-0 rounded-none"
            >
              {sheet.add_card_label_bn}
            </button>
          </div>
        </div>

        {/*
         * Drawn here, with the cash line, as ONE line of `400 13px/1.75` body text.
         * The payload's `partner_name` was previously rendered as a second line; the
         * frame draws no such line, and the sheet is bottom-anchored, so that extra
         * 22.75px pushed every row above it up and put the whole screen out of
         * register. It also disclosed nothing extra: the payload's `partner_name` is
         * the very phrase this sentence already contains (অংশীদার প্রতিষ্ঠান), so
         * docs/05 § fulfilment is still satisfied by the drawn string itself.
         */}
        <div className="bg-sand border border-border px-[13px] py-[11px] mt-[16px]">
          <div className="font-anek font-normal text-[13px] leading-[1.75] text-body">
            {PARTNER_ROLE_BN}
          </div>
        </div>

        {selected === undefined ? null : <ManualPanel method={selected} />}

        <div className="mt-[16px]">
          <Button onClick={() => onConfirm?.(methodId)}>{sheet.confirm_label_bn}</Button>
        </div>
      </div>
    </Frame>
  );
}

export function D5Payment(): JSX.Element {
  const navigate = useNavigate();
  const load = useCallback((signal: AbortSignal) => commerce.quote(signal), []);
  const [resource] = useResource(load, 'quote');

  /**
   * ONE key for this checkout, minted when the screen mounts and reused by every tap.
   *
   * That is the whole idempotency contract from the client's side: the key identifies
   * the INTENT, not the request. Minting it inside the click handler would give each tap
   * its own key, which is the same as having none.
   */
  const [idempotencyKey] = useState(orderIdempotencyKey);

  const confirm = (methodId: string): void => {
    void commerce
      .placeOrder(methodId, idempotencyKey)
      .then((order) => {
        // The reference travels in router state so D6 shows the order that was just
        // placed rather than the latest one, which is the same row only most of the time.
        navigate('/checkout/done', { state: { reference: order.reference } });
      })
      .catch(() => undefined);
  };

  return (
    <Loaded resource={resource}>
      {(quote) => <D5PaymentView quote={quote} onConfirm={confirm} />}
    </Loaded>
  );
}
